import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// @material-ui/core components
import {
  createCaseStudyAPI,
  getAllCaseStudiesAPI,
  cloneCaseStudyAPI,
  addTagsAPI,
  deleteCaseStudyAPI,
} from "views/APIs/CaseStudyUtility";
import { inviteUserAPI } from "views/APIs/APIUtility";

// @material-ui/icons
// import ContentCopy from "@material-ui/icons/ContentCopy";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
} from "@material-ui/core";

// core components

import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import { CloudUpload, Create, DoneAll } from "@material-ui/icons";
import CreateCourseCard from "views/Components/CreateCourseCard";
import AssetDashboard from "views/Components/AssetDashboard";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import CreateFormModal from "views/Components/CreateFormModal";
import { fetchImageAsFile } from "views/Dashboard/Course/CourseConstants";
import FormDetails from "../Models/FormDetails";
import CreateCaseStudyModal from "./CreateCaseStudyModal";

const useStyles = makeStyles({
  ...styles,
});

export default function CaseStudyDashboard() {
  const asset_identifier = "case_study";
  const images = require.context("assets/img/create_cards_16_9/", true);

  // state variables
  const { path, url } = useRouteMatch();
  const classes = useStyles();
  const [createModal, setCreateModal] = React.useState(false);
  const [newCaseStudy, setNewCaseStudy] = React.useState(new FormDetails());
  const [iconImage, setIconImage] = React.useState();
  const [allCaseStudies, setAllCaseStudies] = React.useState([]);
  const [initialCaseStudies, setInitialCaseStudies] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });

  const [caseStudyAdded, setCaseStudyAdded] = React.useState(false);
  const { showNotification } = React.useContext(NotificationContext);

  const handleDeleteCaseStudy = async (data) => {
    const formData = new FormData();
    formData.append("case_study_id", data._id);

    try {
      await deleteCaseStudyAPI(formData);
      setAllCaseStudies(
        allCaseStudies.filter((prevForm) => prevForm._id !== data._id)
      );
      setInitialCaseStudies(
        initialCaseStudies.filter((prevForm) => prevForm._id !== data._id)
      );
      showNotification("Case Study deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleCloneCaseStudy = async (data) => {
    try {
      setLoading({ loading: true, message: "Cloning Case Study..." });
      const formData = new FormData();
      formData.append("case_study_id", data._id);
      const clonedData = await cloneCaseStudyAPI(formData);
      setAllCaseStudies((prevCaseStudies) => [
        ...prevCaseStudies,
        _mapResourceToAppType(clonedData),
      ]);
      setCaseStudyAdded(true);
      showNotification("Case Study cloned successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const requestData = {
        collection_type: "case_study_design",
        project_id: data._id,
        invitee_email: data.email,
      };

      const response = await inviteUserAPI(requestData);
      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allCaseStudies.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        console.warn(
          "Could not find the matching asset for ID:",
          updatedProjectId
        );
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };
      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allCourses state
      const updatedCaseStudies = allCaseStudies.map((caseStudy) =>
        caseStudy._id === updatedProjectId ? newAsset : caseStudy
      );

      setAllCaseStudies(updatedCaseStudies);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      console.error("Error sending invite:", error);
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch all Forms
      try {
        setLoading({ loading: true, message: "Loading Case Studies..." });
        const data = await getAllCaseStudiesAPI();
        const mappedData = data.map(_mapResourceToAppType);
        setAllCaseStudies(mappedData);
        setInitialCaseStudies(mappedData);
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchData();
  }, []);

  const handleCreateCaseStudy = async () => {
    setLoading({ loading: true, message: "Creating Case Study..." });
    const formData = new FormData();
    formData.append("name", newCaseStudy.name);
    formData.append("description", newCaseStudy.description);

    try {
      const data = await createCaseStudyAPI(formData);
      window.open(`/create/case_study/${data._id}`, "_blank");

      setAllCaseStudies([...allCaseStudies, _mapResourceToAppType(data)]);
      setCaseStudyAdded(true);
      showNotification("Case Study created successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      handleCreateCaseStudyModalClose();
      setLoading({ loading: false, message: "" });
    }
  };

  const clearCreateModal = () => {
    setNewCaseStudy(new FormDetails());
    setIconImage(null);
  };

  const handleCreateCaseStudyModalClose = () => {
    setCreateModal(false);
    // setModal(false);
    clearCreateModal();
  };

  const handleFileUpload = (files) => {
    setNewCaseStudy({ ...newCaseStudy, uploadedFiles: files });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "In Design Phase":
        return <Create style={{ color: "#435966" }} />;
      case "Ready for Publishing":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      default:
        return <Create style={{ color: "#435966" }} />;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude course_name and course_description
    const { name, description, ...rest } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: name, // Add name property
      description: description, // Add description property
    };
  };

  const handleSaveFormTags = async (data) => {
    try {
      const formData = new FormData();
      formData.append("case_study_id", data._id);
      if (data.tags.length === 0) {
        formData.append("tags", []);
      }
      // Append each tag separately
      else {
        data.tags.forEach((tag) => formData.append("tags", tag));
      }
      await addTagsAPI(formData);
      setAllCaseStudies((prevCaseStudy) =>
        prevCaseStudy.map((caseStudy) =>
          caseStudy._id === data._id
            ? { ...caseStudy, tags: data.tags }
            : caseStudy
        )
      );
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreateCaseStudyModal
        newCaseStudy={newCaseStudy}
        setNewCaseStudy={setNewCaseStudy}
        createModal={createModal}
        handleCreateModalClose={handleCreateCaseStudyModalClose}
        handleCreateCaseStudy={handleCreateCaseStudy}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Case Study Generator Dashboard"
        welcomeSubtitle="Start by creating a new Case Study or reviewing your existing Case Studies."
        allAssets={allCaseStudies}
        setAllAssets={setAllCaseStudies}
        initialAssets={initialCaseStudies}
        noAssetMessage="Create or Review your Case Studies to get started."
        linkPath="/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateModal}
        createAssetButtonText="Create Case Study"
        handleSaveLabel={handleSaveFormTags}
        handleInviteSend={handleInviteSend}
        asset_identifier={asset_identifier}
        handleDeleteAsset={handleDeleteCaseStudy}
        handleCloneAsset={handleCloneCaseStudy}
        assetAdded={caseStudyAdded}
        setAssetAdded={setCaseStudyAdded}
        setInitialAssets={setInitialCaseStudies}
        workflowConfig={{
          type: "case_study",
          title: "Case Study Generator",
          description: `Generate a Harvard Business School-style case study for your organization.`,
          steps: [
            {
              label: "1. Ideate case & provide details",
              body: "Define the initial case brief and establish core context to frame the narrative.",
            },
            {
              label: "2. Generate ideas & curate research",
              body:
                "Explore candidate ideas and refine the strongest idea to align with goals.",
            },
            {
              label: "3. Generate case study",
              body: "Produce the HBS-style case study.",
            },
          ],
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/case-study-demo.mp4",
          examples: [],
        }}
      />
    </div>
  );
}
